import { FuuzeLoading, queryKeys, User } from '@fuuze/shared';
import { useQueryClient } from '@tanstack/react-query';
import { type User as FirebaseUser, getAuth, type Unsubscribe } from 'firebase/auth';
import { type PropsWithChildren, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { fetchService } from '../../singletons';
import { useAnalyticsReporter, useStore } from '../../store/store';

export default function RedirectUserGuard(props: PropsWithChildren) {
  const location = useLocation();
  const { i18n } = useTranslation();
  const [isInit, setIsInit] = useState(false);
  const queryClient = useQueryClient();
  const analyticsReporter = useAnalyticsReporter();
  const [setPathBeforeLogin, setPodcastSelected] = useStore(
    useShallow(state => [state.setPathBeforeLogin, state.setPodcastSelected]),
  );

  const [pageToRedirect, setPageToRedirect] = useState<{ path: string; replace?: boolean } | undefined>(undefined);

  const redirectToLoginPage = () => {
    setPathBeforeLogin(location.pathname);
    setPageToRedirect({ path: '/login', replace: true });
  };

  useEffect(() => {
    const auth = getAuth();
    auth.languageCode = i18n.resolvedLanguage?.slice(0, 2) || i18n.language;

    const onIdTokenChanged = async (firebaseUser: FirebaseUser | null) => {
      if (!firebaseUser) {
        setIsInit(true);
        return;
      }

      if (!firebaseUser.emailVerified) {
        setPageToRedirect({ path: '/confirm-email', replace: true });
        setIsInit(true);
        return;
      }

      let user: User | undefined;
      try {
        // Email verified, check if user exists in db
        const userDto = await queryClient.fetchQuery<User | undefined>({
          queryKey: queryKeys.user.signIn(),
          queryFn: () => fetchService.get('/user/signIn'),
          retry: 3,
          retryDelay: failureCount => Math.min(1000 * 3 ** failureCount, 30000),
        });
        user = userDto ? new User(userDto) : undefined;
      } catch (e) {
        console.error(e);
        setIsInit(true);
        redirectToLoginPage();
        return;
      }

      if (!user) {
        redirectToLoginPage();
        setIsInit(true);
        return;
      }

      analyticsReporter.identifyUser({ id: user._id, pseudo: user.pseudo });

      if (user.deletionRequestDate) {
        setPageToRedirect({ path: '/account-under-cancellation', replace: true });
        setIsInit(true);
        return;
      }

      // User ok
      if (user.ownedChannels.length > 0) {
        setPodcastSelected(user.ownedChannels[0]!._id, user.ownedChannels[0]!.slug);
      }
      setIsInit(true);
    };

    let unsubscribeAuth: Unsubscribe;
    const asyncWork = () => {
      unsubscribeAuth = auth.onIdTokenChanged(onIdTokenChanged);
    };

    asyncWork();

    return () => {
      unsubscribeAuth();
    };
  }, []);

  if (!isInit)
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100dvh' }}>
        <FuuzeLoading $size={80} $color='orange' $bgColor='transparent' />
      </div>
    );

  if (pageToRedirect) {
    return <Navigate to={pageToRedirect.path} replace={pageToRedirect.replace} />;
  }

  return props.children;
}
